import React, { memo, useCallback, useRef } from 'react';
import { LinkIt, urlRegex } from 'react-linkify-it';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { PromotionReplyDto } from '@diritto/common/promotion/dtos/promotion-reply.dto';
import { ProfileDto } from '@diritto/common/account/dtos/profile.dto';

import { ProfileImage } from 'app-components/accounts/profiles/ProfileImage';
import { EMPTY_PROFILE_TEXT } from 'constants/profile';
import { getTextLineStyle } from 'styles/common';
import { Divider } from 'ui-components/common/Divider';
import { ExternalLink } from 'ui-components/common/ExternalLink';
import { Link } from 'ui-components/common/Link';
import { RelativeDateText } from 'ui-components/common/RelativeDateText';
import { PromotionReplyReaction } from '@/app-components/promotions/PromotionReplyReaction';
import { PromotionReplyKeyword } from '@/app-components/promotions/PromotionReplyKeyword';

interface PromotionReplyProps {
  className?: string;
  promotionId: number;
  reply: PromotionReplyDto;
  profile: ProfileDto;
  isLiked: boolean;
  refetch: () => void;
}

const makeExternalLink = (match: string, key: number) => (
  <ExternalLink href={match} key={key}>
    <StyledLink>{match}</StyledLink>
  </ExternalLink>
);

export const PromotionReply = memo(
  ({ className, promotionId, reply, profile, isLiked, refetch }: PromotionReplyProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const handleDeleteSuccess = useCallback(() => {
      refetch();
    }, [refetch]);

    return (
      <StyledContainer id={String(reply.id)} ref={ref} className={className}>
        <StyledProfile>
          {profile ? (
            <Link href={`/accounts/profiles/${profile.id}`}>
              <ProfileImage file={profile?.profileImage} defaultImageKey={profile?.nickname} size={32} />
            </Link>
          ) : (
            <ProfileImage size={32} />
          )}
        </StyledProfile>
        <StyledReplyInfo>
          <StyledTop>
            <StyledName>
              {profile ? <Link href={`/accounts/profiles/${profile.id}`}>{profile.nickname}</Link> : EMPTY_PROFILE_TEXT}
            </StyledName>
            <StyledDate date={reply.createdAt} />
          </StyledTop>
          <StyledDescription>
            <LinkIt component={makeExternalLink} regex={urlRegex}>
              {reply.description}
            </LinkIt>
          </StyledDescription>
          <StyledKeywordWrapper>
            <PromotionReplyKeyword keyword={reply.item} />
          </StyledKeywordWrapper>
          <PromotionReplyReaction
            promotionId={promotionId}
            reply={reply}
            isLiked={isLiked}
            onDeletedSuccess={handleDeleteSuccess}
            showLike
            showDelete
          />
        </StyledReplyInfo>
        <StyledDivider position="bottom" color={Color.Gray200} />
      </StyledContainer>
    );
  },
);

PromotionReply.displayName = 'PromotionReply';

const StyledContainer = styled.div(
  ({ isMyReply, isClickable }: { isMyReply: boolean; isClickable: boolean }) => css`
    display: flex;
    background-color: ${isMyReply ? Color.Gray100 : Color.White};
    position: relative;
    padding: 16px 0px;
    width: 100%;
    cursor: ${isClickable ? 'pointer' : 'default'};
  `,
);

const StyledLink = styled.span`
  color: ${Color.Blue500};
  text-decoration: underline;
  word-break: break-all;
  white-space: break-spaces;
  word-wrap: anywhere;
`;

const StyledDivider = styled(Divider)`
  width: 100%;
`;

const StyledProfile = styled.div`
  flex-shrink: 0;
`;

const StyledReplyInfo = styled.div`
  width: calc(100% - 32px - 16px - 16px);
  margin-left: 16px;
  position: relative;
  flex-grow: 1;
`;

const StyledTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledName = styled.div`
  display: flex;
  align-items: center;
  ${Typography.XXSmall};

  & > a {
    margin-right: 4px;
    width: fit-content;
    font-weight: ${FontWeight.Bold};
    ${getTextLineStyle(1)};
  }

  & > p {
    margin-right: 4px;
    width: fit-content;
    font-weight: ${FontWeight.Bold};
    ${getTextLineStyle(1)};
  }
`;

const StyledDate = styled(RelativeDateText)`
  width: fit-content;
  margin-left: 10px;
  font-weight: ${FontWeight.Regular};
  color: ${Color.Gray500};
  ${Typography.XXXSmall};
  ${getTextLineStyle(1)};
`;

const StyledKeywordWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  margin-top: 8px;
`;

const StyledDescription = styled.p`
  margin-top: 4px;
  font-weight: ${FontWeight.Regular};
  color: ${Color.Gray900};
  ${Typography.XXSmall}
  word-break: break-all;
  white-space: break-spaces;
`;
